import { createContext, useContext, useMemo } from 'react';
import { constants } from '@eti/utils';
import { useMediaQuery } from 'usehooks-ts';
import { useProperty } from '../properties/PropertiesContext';
import { jsx as _jsx } from "react/jsx-runtime";
const {
  brands
} = constants;
const SiteContext = /*#__PURE__*/createContext(null);
const defaultProps = {
  dateFormat: {
    date: 'yyyy-MM-dd',
    datetime: 'yyyy-MM-dd HH:mm',
    dayDdmmmTime: 'ddMMM HH:mm',
    dayDdmmm: 'ddMMM',
    time: 'HH:mm',
    day: 'd MMM',
    dayYear: 'd MMM yyyy',
    dayTime: 'd MMM HH:mm',
    weekday: 'EEEE d MMM',
    shortWeekday: 'E d MMM',
    shortWeekdayYear: 'E d MMM yyyy',
    shortWeekdayFullYear: 'E d MMMM, yyyy',
    weekdayYear: 'EEEE d MMMM, yyyy',
    monthYear: 'MMM yyyy',
    longMonthYear: 'MMMM yyyy',
    timeWeekdayYear: 'HH:mm EEEE d MMMM, yyyy',
    shortDayShortMonth: 'E d MMM',
    dayName: 'EEEE'
  },
  timeFormat: {
    hour: 'h',
    minute: 'min',
    second: 's'
  },
  language: {
    code: 'en',
    name: 'English',
    locale: 'en_GB'
  },
  brand: {
    code: 'GOTOGATE',
    name: 'GoToGate/FB'
  }
};
const SiteContextProvider = ({
  agencyAccount,
  availableLanguages,
  brand = defaultProps.brand,
  dateFormat = defaultProps.dateFormat,
  entryType,
  headerAndInfoHeight = 0,
  language = defaultProps.language,
  market,
  partner,
  siteCurrency,
  siteName,
  timeFormat = defaultProps.timeFormat,
  ...rest
}) => {
  const contextValue = useMemo(() => ({
    agencyAccount,
    availableLanguages,
    brand,
    dateFormat,
    entryType,
    headerAndInfoHeight,
    language,
    market,
    partner,
    siteCurrency,
    siteName,
    timeFormat
  }), [agencyAccount, availableLanguages, brand, dateFormat, entryType, headerAndInfoHeight, language, market, partner, siteCurrency, siteName, timeFormat]);
  return /*#__PURE__*/_jsx(SiteContext.Provider, {
    value: contextValue,
    ...rest
  });
};
const generateErrorMessage = text => `${text} must be used within a SiteContextProvider`;

// TODO remove this once the data is normalized on IBE: https://jira.etraveli.net/browse/WEB-1981
const normalizeBrandCode = brand => {
  var _brand$code;
  const code = ((_brand$code = brand === null || brand === void 0 ? void 0 : brand.code) !== null && _brand$code !== void 0 ? _brand$code : '').toLowerCase();
  return {
    ...brand,
    code
  };
};
const useDateFormat = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error(generateErrorMessage('useDateFormat'));
  }
  return useMemo(() => context.dateFormat, [context.dateFormat]);
};
const useRevolut = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error(generateErrorMessage('useRevolut'));
  }
  const {
    p
  } = useProperty();
  const {
    brand
  } = context;
  const normalizedBrand = normalizeBrandCode(brand);
  return {
    isRevolut: normalizedBrand.code === brands.REVOLUT,
    hasAppPageHeader: normalizedBrand.code === brands.REVOLUT && p('IbeClient.MenuAndFooter.Hide')
  };
};
const useSiteContext = () => {
  const context = useContext(SiteContext);
  if (!context) {
    throw new Error(generateErrorMessage('useSiteContext'));
  }
  const {
    agencyAccount,
    availableLanguages,
    brand,
    entryType,
    headerAndInfoHeight,
    language,
    market,
    partner,
    siteCurrency,
    siteName,
    timeFormat
  } = context;
  return useMemo(() => {
    return {
      agencyAccount,
      availableLanguages,
      brand: normalizeBrandCode(brand),
      entryType,
      headerAndInfoHeight,
      language,
      market,
      partner,
      siteCurrency,
      siteName,
      timeFormat
    };
  }, [agencyAccount, availableLanguages, brand, entryType, headerAndInfoHeight, language, market, partner, siteCurrency, siteName, timeFormat]);
};
const useDarkMode = () => {
  const {
    p
  } = useProperty();
  const isDarkModeEnabled = p('CustomerWeb.UseDarkModeTheme.Enabled');
  const isSystemDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  return {
    isDarkMode: isDarkModeEnabled && isSystemDarkMode
  };
};
export { SiteContextProvider, useDateFormat, useRevolut, useSiteContext, useDarkMode };
//# sourceMappingURL=SiteContext.js.map